import { useState, type FormEvent } from 'react'
import { Navigate, Link, useLocation } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { authRepository } from './api/auth-repository'
import { useAuthStore } from '@/core/auth/auth-store'
import { Button } from '@/shared/ui/button'
import { BrandMark } from '@/shared/components/brand-mark'
import { useAppReady } from '@/shared/hooks/use-app-ready'

type BusyAction = 'google' | 'phone' | 'email' | null

// Minimal player sign-in: Google one-tap + phone + email/password (sign-in /
// sign-up toggle). Each action shows its own spinner + disabled state while
// busy. The auth listener flips the store on success → redirect.
export function LoginPage() {
  useAppReady()
  const status = useAuthStore((s) => s.status)
  const isAnonymous = useAuthStore((s) => s.user?.isAnonymous ?? false)
  // Callers can open this screen straight into signup. The share door's
  // dead-link state does: someone who tapped a padel link in WhatsApp and found
  // an expired match has no account by definition, so landing them on "Entrar"
  // makes them find the toggle before they can do anything.
  const navState = useLocation().state as { mode?: 'signin' | 'signup' } | null
  const [mode, setMode] = useState<'signin' | 'signup'>(navState?.mode ?? 'signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState<BusyAction>(null)
  const [error, setError] = useState<string | null>(null)

  // An ANONYMOUS session counts as `authenticated`, and the share door mints one
  // silently on every visit. So this used to make /login UNREACHABLE for anyone
  // who had opened a shared link: it bounced them to "/", which is protected,
  // which sent them to /onboarding — a returning player with a real account,
  // permanently routed away from the form that would sign them in.
  // Only a REAL account should be redirected off the login screen. An anonymous
  // visitor signing in here is linked onto the same uid by `authRepository`.
  if (status === 'authenticated' && !isAnonymous) return <Navigate to="/" replace />

  const working = busy !== null

  async function run(action: Exclude<BusyAction, null>, fn: () => Promise<unknown>) {
    setBusy(action)
    setError(null)
    try {
      await fn()
    } catch {
      setError('No pudimos iniciar sesión. Revisa tus datos e inténtalo de nuevo.')
    } finally {
      setBusy(null)
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    void run('email', () =>
      mode === 'signin'
        ? authRepository.signIn(email, password)
        : authRepository.createUser(email, password),
    )
  }

  return (
    <main className="min-h-full flex flex-col items-center justify-center px-6 safe-top safe-bottom">
      <div className="w-full max-w-sm flex flex-col items-center gap-6 um-fade">
        <div className="text-[color:var(--brand-mint)]">
          <BrandMark size={72} />
        </div>
        <div className="text-center">
          <h1 className="text-[length:var(--fs-2xl)] font-bold">uPadelMatch</h1>
          <p className="text-[color:var(--fg-muted)] mt-1">Encuentra tu próximo partido.</p>
        </div>

        <Button
          size="lg"
          variant="outline"
          disabled={working}
          aria-busy={busy === 'google'}
          // Wrapped, not passed by reference: detaching the method from the
          // repository drops `this`. Safe today (the impl closes over `auth`),
          // but that is an implementation detail this call site shouldn't rely on.
          onClick={() => void run('google', () => authRepository.signInWithGoogle())}
        >
          {busy === 'google' && <Loader2 size={18} className="motion-safe:animate-spin" />}
          Continuar con Google
        </Button>

        {/* Phone OTP login removed for v1 — the flow was a design stub that
            accepted any code (truth defect, release item R3). Real phone
            auth returns with roadmap #21. */}

        <div className="flex items-center gap-3 w-full text-[color:var(--fg-faint)] text-[length:var(--fs-xs)]">
          <span className="h-px flex-1 bg-[var(--line)]" /> o <span className="h-px flex-1 bg-[var(--line)]" />
        </div>

        <form onSubmit={onSubmit} className="w-full flex flex-col gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-[length:var(--fs-sm)] font-semibold text-[color:var(--fg-muted)]">Correo</span>
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="tucorreo@ejemplo.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="field"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[length:var(--fs-sm)] font-semibold text-[color:var(--fg-muted)]">Contraseña</span>
            <input
              type="password"
              autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="field"
            />
          </label>
          {error && <p className="text-[color:var(--bad)] text-[length:var(--fs-sm)]">{error}</p>}
          <Button type="submit" size="lg" disabled={working} aria-busy={busy === 'email'}>
            {busy === 'email' && <Loader2 size={18} className="motion-safe:animate-spin" />}
            {mode === 'signin' ? 'Entrar' : 'Crear cuenta'}
          </Button>
        </form>

        {/* 48 px like the toggle right below it — measured at 18 px on live
            prod. This is the recovery path for someone who cannot get in, so
            it is the last link in the app that should be hard to hit. (Login is
            invisible to `dev:preview`, which is why it went unaudited: the
            bypass lands you straight in the feed.) */}
        {mode === 'signin' && (
          <Link
            to="/forgot-password"
            className="text-[color:var(--fg-muted)] text-[length:var(--fs-sm)] min-h-[var(--tap)] inline-flex items-center"
          >
            ¿Olvidaste tu contraseña?
          </Link>
        )}

        <button
          type="button"
          className="text-[color:var(--accent)] text-[length:var(--fs-sm)] min-h-[var(--tap)]"
          onClick={() => setMode((m) => (m === 'signin' ? 'signup' : 'signin'))}
        >
          {mode === 'signin' ? '¿No tienes cuenta? Créala' : '¿Ya tienes cuenta? Entra'}
        </button>

        {/* One line at 390px — short link labels + nowrap so a wrap on a
            narrower phone can never split a link name mid-phrase. */}
        <p className="text-center text-[length:var(--fs-xs)] text-[color:var(--fg-faint)]">
          Al continuar aceptas los{' '}
          <Link to="/terminos" className="underline whitespace-nowrap text-[color:var(--fg-muted)]">
            Términos
          </Link>{' '}
          y la{' '}
          <Link to="/privacidad" className="underline whitespace-nowrap text-[color:var(--fg-muted)]">
            Privacidad
          </Link>
          .
        </p>
      </div>
    </main>
  )
}
